// LLM client. In the production build this ALWAYS talks to /api/llm (the
// Vercel serverless function), which validates the Supabase session and hides
// the Anthropic key. There are no client-side Anthropic/OpenRouter paths in
// this build — the API-keys sheet is dev-only.
//
// In a native Capacitor build (iOS/Android) the app is served from
// capacitor://localhost, so a relative '/api/llm' would 404. We resolve to the
// absolute Vercel deployment URL when running on a native platform.

import { apiUrl } from './platform.js';
import { getFreshAccessToken, forceRefreshAccessToken } from './supabase.js';
import { ensureAIConsent } from './aiConsent.js';
import { handleApiResponse } from './subscription.js';

// Custom error so callers can distinguish a paywall block from a real
// upstream failure. Gating UI (briefing scene, intelligence scene) catches
// this and silently returns — the global PaywallSheet is already on screen.
export class PaywallRequiredError extends Error {
  constructor(usage = null) {
    super('Paywall required');
    this.name = 'PaywallRequiredError';
    this.usage = usage;
  }
}

// apiUrl is provided by ./platform.js — returns an absolute Vercel URL on
// iOS/Electron (where the page is loaded from capacitor:// or file://) and
// a relative path on the web (where Vercel serves both the SPA and /api).

// Run `doFetch(token)` with a freshly-validated token. If the server
// still returns 401 — meaning the stored session was accepted locally
// but rejected by Supabase (expired between us refreshing and the
// request arriving, or session was revoked server-side) — force one
// more refresh and retry. After that, surrender and let the caller
// surface "session expired."
async function authedFetch(doFetch, guestMessage) {
  const token = await getFreshAccessToken();
  if (!token) throw new Error(guestMessage);

  let res = await doFetch(token);
  if (res.status !== 401) return res;

  const retryToken = await forceRefreshAccessToken();
  if (!retryToken) return res; // fall through; caller throws "session expired"
  return await doFetch(retryToken);
}

export async function callLLM({ system, messages, model }) {
  // Apple 5.1.1(i)/5.1.2(i): user must grant in-app consent before
  // anything goes to Anthropic. ensureAIConsent() resolves immediately
  // if consent is already on file; otherwise it opens the global modal
  // and throws AIConsentDeclinedError if the user cancels.
  await ensureAIConsent();

  const res = await authedFetch(
    (token) => fetch(apiUrl('/api/llm'), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ system, messages, model }),
    }),
    'Please sign in to use AI features.'
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 402) {
      // Free-tier cap hit. Show the paywall and bail without surfacing
      // a scary error toast — the sheet is doing the talking.
      handleApiResponse(res, err, 'llm_limit');
      throw new PaywallRequiredError(err?.usage);
    }
    if (res.status === 429) throw new Error(err.error || 'Daily AI request limit reached.');
    if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
    throw new Error(err.error || `AI service error (${res.status})`);
  }
  const data = await res.json();
  return data?.text || '';
}

// ── transcribeAudio ────────────────────────────────────────────────
// Sends a recorded audio Blob to /api/stt (OpenAI Whisper proxy) and
// returns the transcribed text. The Blob's MIME type is forwarded as
// Content-Type so the server can hint Whisper at the right decoder.
export async function transcribeAudio(blob) {
  if (!blob || !blob.size) throw new Error('No audio captured.');

  // Voice input goes to OpenAI Whisper via /api/stt — same disclosure
  // requirement as the Claude path above.
  await ensureAIConsent();

  const res = await authedFetch(
    (token) => fetch(apiUrl('/api/stt'), {
      method: 'POST',
      headers: {
        'content-type': blob.type || 'audio/webm',
        'authorization': `Bearer ${token}`,
      },
      body: blob,
    }),
    'Please sign in to use voice input.'
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 402) {
      handleApiResponse(res, err, 'stt_limit');
      throw new PaywallRequiredError(err?.usage);
    }
    if (res.status === 429) throw new Error(err.error || 'Daily voice limit reached.');
    if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
    throw new Error(err.error || `Voice service error (${res.status})`);
  }
  const data = await res.json();
  return (data?.text || '').trim();
}
